import { Box, Stack, Typography } from '@mui/material';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals, SemanticError } from '../../../../core-components/theme/color-palette/colors';
import { quietProblemMessage, windowSummaryLabel } from '../../constants/labels';
import { timePairSx } from '../../constants/layout';
import type { QuietWindowFailure, QuietWindowField, WindowSummary } from '../../domain/quietHours';

import { ClockInput } from './ClockInput';

const messageSx = { color: SemanticError[600] } as const;
const summarySx = { color: Neutrals[600], fontVariantNumeric: 'tabular-nums' } as const;

export interface QuietWindowRefs {
  readonly from?: React.Ref<HTMLInputElement>;
  readonly to?: React.Ref<HTMLInputElement>;
}

export interface QuietWindowFieldsProps {
  readonly from: string;
  readonly to: string;
  readonly invalid: QuietWindowFailure | null;
  readonly summary: WindowSummary | null;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly fieldRefs?: QuietWindowRefs;
  readonly onFromChange: (value: string) => void;
  readonly onToChange: (value: string) => void;
  readonly onBlur?: (field: QuietWindowField) => void;
}

const WindowProblem: React.FC<{ id: string; message: string | null }> = ({ id, message }) => {
  if (message === null) return null;
  return (
    <Typography id={id} variant="caption2" role="alert" sx={messageSx}>
      {message}
    </Typography>
  );
};

const WindowSummaryLine: React.FC<{ summary: WindowSummary | null }> = ({ summary }) => {
  const { t } = useTranslation();
  if (summary === null) return null;
  return (
    <Typography variant="caption2" sx={summarySx}>
      {windowSummaryLabel(t, summary)}
    </Typography>
  );
};

export const QuietWindowFields: React.FC<QuietWindowFieldsProps> = ({
  from,
  to,
  invalid,
  summary,
  disabled = false,
  readOnly = false,
  fieldRefs,
  onFromChange,
  onToChange,
  onBlur,
}) => {
  const { t } = useTranslation();
  const messageId = useId();
  const message = invalid === null ? null : quietProblemMessage(t, invalid.problem);
  const describedBy = message === null ? undefined : messageId;
  const shownSummary = message === null ? summary : null;

  return (
    <Stack spacing={2} useFlexGap>
      <Box sx={timePairSx}>
        <ClockInput
          label={t('closeTheLoop.quietHours.from', 'From')}
          value={from}
          disabled={disabled}
          readOnly={readOnly}
          error={invalid?.from != null}
          describedById={describedBy}
          inputRef={fieldRefs?.from}
          onValueChange={onFromChange}
          onBlur={onBlur && (() => onBlur('from'))}
        />
        <ClockInput
          label={t('closeTheLoop.quietHours.to', 'To')}
          value={to}
          disabled={disabled}
          readOnly={readOnly}
          error={invalid?.to != null}
          describedById={describedBy}
          inputRef={fieldRefs?.to}
          helperText={shownSummary?.overnight ? t('closeTheLoop.quietHours.nextMorning', 'next morning') : undefined}
          onValueChange={onToChange}
          onBlur={onBlur && (() => onBlur('to'))}
        />
      </Box>
      <WindowProblem id={messageId} message={message} />
      <WindowSummaryLine summary={shownSummary} />
    </Stack>
  );
};
